import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { School, People, Delete } from '@mui/icons-material';

interface TeamsOverviewProps {
  wrestlers: Array<{ schoolName: string; firstName: string; lastName: string; weightKg: number; age?: number; grade?: number | string }>;
  onRemoveSchool: (schoolName: string) => void;
  weightUnit?: 'kg' | 'lbs';
}

const TeamsOverview: React.FC<TeamsOverviewProps> = ({ wrestlers, onRemoveSchool, weightUnit = 'kg' }) => {
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; schoolName: string }>({ open: false, schoolName: '' });
  // Group wrestlers by school and calculate stats
  const schoolData = wrestlers.reduce((acc, wrestler) => {
    if (!acc[wrestler.schoolName]) {
      acc[wrestler.schoolName] = {
        name: wrestler.schoolName,
        wrestlers: [],
        totalWeight: 0,
        avgWeight: 0,
        gradeDistribution: {} as Record<string, number>,
      };
    }
    
    acc[wrestler.schoolName].wrestlers.push(wrestler);
    acc[wrestler.schoolName].totalWeight += wrestler.weightKg;
    
    const grade = wrestler.grade?.toString() || 'Unknown';
    acc[wrestler.schoolName].gradeDistribution[grade] = (acc[wrestler.schoolName].gradeDistribution[grade] || 0) + 1;
    
    return acc;
  }, {} as Record<string, any>);

  const displayWeight = (weightKg: number) => {
    return `${weightKg.toFixed(1)}${weightUnit}`;
  };

  // Calculate averages and convert to array
  const schools = Object.values(schoolData).map((school: any) => ({
    ...school,
    avgWeight: school.totalWeight / school.wrestlers.length,
  }));

  // Tournament totals
  const totalSchools = schools.length;
  const totalWrestlers = wrestlers.length;
  const totalWeight = wrestlers.reduce((sum, w) => sum + w.weightKg, 0);
  const overallAvgWeight = totalWrestlers > 0 ? totalWeight / totalWrestlers : 0;

  const handleRemoveSchool = (schoolName: string) => {
    setDeleteDialog({ open: true, schoolName });
  };

  const confirmRemoveSchool = () => {
    if (deleteDialog.schoolName) {
      onRemoveSchool(deleteDialog.schoolName);
      setDeleteDialog({ open: false, schoolName: '' });
    }
  };

  const cancelRemove = () => {
    setDeleteDialog({ open: false, schoolName: '' });
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Teams Overview
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Complete breakdown of all schools and their wrestling teams.
      </Typography>

      {/* Tournament Summary */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Tournament Statistics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <School sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4">{totalSchools}</Typography>
                <Typography variant="body2" color="text.secondary">Total Schools</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <People sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                <Typography variant="h4">{totalWrestlers}</Typography>
                <Typography variant="body2" color="text.secondary">Total Wrestlers</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4">{overallAvgWeight.toFixed(1)}</Typography>
                <Typography variant="body2" color="text.secondary">Avg Weight (kg)</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4">{(totalWeight / 2.2).toFixed(0)}</Typography>
                <Typography variant="body2" color="text.secondary">Total Weight (lbs)</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Schools List */}
      <Typography variant="h5" gutterBottom>
        School Teams ({totalSchools})
      </Typography>

      <Grid container spacing={3}>
        {schools.map((school, index) => (
          <Grid item xs={12} md={6} lg={4} key={school.name}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <School />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{school.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {school.wrestlers.length} wrestlers
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Average Weight:</strong> {school.avgWeight.toFixed(1)} kg ({(school.avgWeight * 2.2).toFixed(0)} lbs)
                </Typography>

                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Grade Distribution:</strong>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {Object.entries(school.gradeDistribution).map(([grade, count]) => (
                    <Chip 
                      key={grade} 
                      label={`Grade ${grade}: ${count}`} 
                      size="small" 
                      variant="outlined"
                    />
                  ))}
                </Box>

                <Typography variant="body2" color="text.secondary">
                  <strong>Top Wrestlers:</strong>
                </Typography>
                <List dense sx={{ maxHeight: 120, overflow: 'auto' }}>
                  {school.wrestlers.slice(0, 3).map((wrestler: any, i: number) => (
                    <ListItem key={i} sx={{ py: 0 }}>
                      <ListItemAvatar sx={{ bgcolor: 'primary.main' }}>
                        {wrestler.firstName[0]}{wrestler.lastName[0]}
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${wrestler.firstName} ${wrestler.lastName}`}
                        secondary={`${wrestler.schoolName} • ${displayWeight(wrestler.weightKg)}${wrestler.grade ? ` • Grade ${wrestler.grade}` : ''}`}
                      />
                    </ListItem>
                  ))}
                  {school.wrestlers.length > 3 && (
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary={`... and ${school.wrestlers.length - 3} more`}
                        secondary=""
                      />
                    </ListItem>
                  )}
                </List>

                <Box sx={{ mt: 'auto', pt: 2 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<Delete />}
                    onClick={() => handleRemoveSchool(school.name)}
                    fullWidth
                  >
                    Remove School
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={cancelRemove}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove School from Tournament</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to remove <strong>{deleteDialog.schoolName}</strong> and all {wrestlers.filter(w => w.schoolName === deleteDialog.schoolName).length} wrestlers from the tournament?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You can re-import this school later using the CSV import feature.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRemove}>
            Cancel
          </Button>
          <Button 
            onClick={confirmRemoveSchool} 
            color="error" 
            variant="contained"
          >
            Remove School
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamsOverview;
